import type { AuthChangeEvent } from '@supabase/supabase-js';

import type { SignOutIntent } from './auth-intent';

/**
 * @name PRIVATE_PATH_PREFIXES
 * @description Route prefixes that require an authenticated session. A forced
 * sign-out on one of these sends the user to sign-in; on any other (public)
 * route there is nothing to protect.
 */
export const PRIVATE_PATH_PREFIXES = [
  '/home',
  '/admin',
  '/join',
  '/identities',
  '/update-password',
];

/**
 * @name AUTH_PATHS
 * @description Paths where a `SIGNED_OUT` event is expected and must be ignored.
 */
const AUTH_PATHS = ['/auth'];

/**
 * @name DEFAULT_SIGN_IN_PATH
 */
export const DEFAULT_SIGN_IN_PATH = '/auth/sign-in';

export type ForcedSignOutPayload = {
  event: AuthChangeEvent;
  pathname: string;
};

/**
 * @name AuthChangeHandlerDeps
 * @description Side effects injected into {@link handleAuthChange}. Keeping
 * these as dependencies makes the branching logic pure and unit-testable
 * without React, the DOM, or a live Supabase client.
 */
export type AuthChangeHandlerDeps = {
  pathname: string;
  search: string;
  privatePathPrefixes: string[];
  signInPath: string;
  /** Read + clear the recorded sign-out intent (one-shot). */
  consumeSignOutIntent: () => SignOutIntent | null;
  /** Hard-navigate the browser (wraps `window.location.assign`). */
  navigate: (url: string) => void;
  onForcedSignOut?: (payload: ForcedSignOutPayload) => void;
};

/**
 * @name isPrivateRoute
 * @description Determines if a given path is a private route.
 */
export function isPrivateRoute(path: string, privatePathPrefixes: string[]) {
  return privatePathPrefixes.some((prefix) => path.startsWith(prefix));
}

/**
 * @name handleAuthChange
 * @description React to a Supabase auth state change.
 *
 * Only `SIGNED_OUT` is actionable. Every other event — including
 * `INITIAL_SESSION`, which supabase-js can emit with a null session before it
 * hydrates — is ignored, so we never act on a transient null. Protected
 * navigations are gated server-side by the middleware regardless.
 *
 * For `SIGNED_OUT`:
 * - We ALWAYS consume the recorded intent first, so it can never leak to a
 *   later event (e.g. a `SIGNED_OUT` emitted while on an `/auth` route).
 * - A `silent` intent (impersonation sign-out→sign-in transition) does nothing
 *   — the flow does its own navigation.
 * - A `navigate` intent (the user clicked "sign out") goes to the marketing
 *   home, matching historic behaviour.
 * - No intent means a spurious/forced sign-out (token-refresh race, idle
 *   expiry, other-tab sign-out — PAY-1006). It can't be recovered: by the time
 *   this runs, Supabase has already cleared the auth cookies. So on a private
 *   route we send the user to sign-in preserving their location via `?next=`
 *   (better than the old reload / `assign('/')`, which lost their place); on a
 *   public route there is nothing to protect, so we do nothing.
 */
export function handleAuthChange(
  event: AuthChangeEvent,
  deps: AuthChangeHandlerDeps,
) {
  if (event !== 'SIGNED_OUT') {
    return;
  }

  // Always consume the intent so it can never leak to a future event.
  const intent = deps.consumeSignOutIntent();

  // Supabase sometimes emits SIGNED_OUT while on an auth path; ignore it (the
  // intent has already been cleared above).
  if (AUTH_PATHS.some((path) => deps.pathname.startsWith(path))) {
    return;
  }

  // Impersonation transition: the flow handles its own navigation.
  if (intent === 'silent') {
    return;
  }

  // User-initiated sign-out: send to the marketing home (historic behaviour).
  if (intent === 'navigate') {
    deps.navigate('/');
    return;
  }

  // Forced/spurious sign-out. Soften it on protected routes; ignore on public.
  if (isPrivateRoute(deps.pathname, deps.privatePathPrefixes)) {
    deps.onForcedSignOut?.({ event, pathname: deps.pathname });

    deps.navigate(buildSignInUrl(deps.signInPath, deps.pathname + deps.search));
  }
}

/**
 * @name buildSignInUrl
 * @description Append a `next` query param to the sign-in path, preserving any
 * existing query string or hash already on it — so a configured sign-in URL
 * with params doesn't produce a double `?` or drop them.
 */
function buildSignInUrl(signInPath: string, next: string): string {
  // Parse against a dummy origin so a relative path with an existing query/hash
  // is handled correctly, then return only the relative portion.
  const url = new URL(signInPath, 'http://internal');
  url.searchParams.set('next', next);

  return `${url.pathname}${url.search}${url.hash}`;
}
